import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from "react";
import { cn } from "@/lib/cn";

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  label?: string;
  /** Help text rendered below the input in muted color. */
  hint?: ReactNode;
  /** Error message rendered below the input; also highlights border. */
  error?: ReactNode;
  /** Icon rendered on the leading edge (inside the input). */
  leadingIcon?: ReactNode;
  /** Trailing element (useful for "show password" button, etc.). */
  trailingElement?: ReactNode;
  containerClassName?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hint,
      error,
      leadingIcon,
      trailingElement,
      id,
      containerClassName,
      className,
      type = "text",
      required,
      ...rest
    },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const describedBy =
      [hintId, errorId].filter(Boolean).join(" ") || undefined;

    return (
      <div className={cn("space-y-1.5", containerClassName)}>
        {label ? (
          <label
            htmlFor={inputId}
            className="block text-caption font-medium text-ink"
          >
            {label}
            {required ? (
              <span className="ml-0.5 text-danger-500" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>
        ) : null}

        <div className="relative">
          {leadingIcon ? (
            <span
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            >
              {leadingIcon}
            </span>
          ) : null}

          <input
            {...rest}
            ref={ref}
            id={inputId}
            type={type}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              "block h-11 w-full rounded-md bg-surface-subtle px-3.5 text-body text-ink placeholder:text-ink-placeholder",
              "border border-transparent transition-colors",
              "focus:bg-surface-card focus:border-brand-500 focus:shadow-focus focus:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-60",
              leadingIcon && "pl-10",
              trailingElement && "pr-10",
              error && "border-danger-500 focus:border-danger-500 focus:shadow-focus-danger",
              className,
            )}
          />

          {trailingElement ? (
            <span className="absolute right-2 top-1/2 -translate-y-1/2">
              {trailingElement}
            </span>
          ) : null}
        </div>

        {hint && !error ? (
          <p id={hintId} className="text-caption text-ink-subtle">
            {hint}
          </p>
        ) : null}

        {error ? (
          <p id={errorId} className="text-caption text-danger-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
